import { useState } from 'react';
import { Download, Share, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useInstallPrompt } from '@/lib/pwa';

/**
 * Floating install button + iOS hint card.
 *
 * Rules:
 *  - If already installed (standalone mode) → render nothing
 *  - If Chrome/Edge/Android: show a small button bottom-right that calls
 *    the native install prompt
 *  - If iOS Safari: show a one-time dismissible card with the manual steps
 *
 * Dismissal is remembered in localStorage so we don't nag.
 */
export default function InstallPrompt() {
  const { canInstall, installed, promptInstall, isIos } = useInstallPrompt();
  const [dismissedIos, setDismissedIos] = useState(() => {
    try {
      return localStorage.getItem('pwa_ios_hint_dismissed') === '1';
    } catch {
      return false;
    }
  });

  if (installed) return null;

  // iOS — manual install hint
  if (isIos && !dismissedIos) {
    const dismiss = () => {
      try { localStorage.setItem('pwa_ios_hint_dismissed', '1'); } catch { /* ignore */ }
      setDismissedIos(true);
    };
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 bg-card border border-border rounded-2xl shadow-lg p-4 max-w-sm mx-auto"
           dir="rtl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-foreground text-sm">ثبّت SellGo على الشاشة الرئيسية</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              اضغط <Share className="w-3 h-3 inline-block align-middle text-blue-500" /> ثم
              <span className="font-semibold"> Add to Home Screen </span>
              <Plus className="w-3 h-3 inline-block align-middle" />
            </p>
          </div>
          <button onClick={dismiss} className="text-muted-foreground hover:text-foreground p-1 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Chrome / Edge / Android — native install button
  if (canInstall) {
    return (
      <button
        onClick={promptInstall}
        className="fixed bottom-20 left-4 z-50 bg-primary text-white rounded-full shadow-lg shadow-primary/30 px-4 py-3 flex items-center gap-2 hover:bg-primary/90 transition-all"
        dir="rtl"
      >
        <Download className="w-4 h-4" />
        <span className="text-sm font-semibold">تثبيت التطبيق</span>
      </button>
    );
  }

  return null;
}
